import { gql } from '@apollo/client'

const SITE_DIARY_ATTRIBUTES = gql`
  fragment siteDiaryAttributes on SiteDiary {
    id
    createdAt
    archivedAt

    date

    createdBy {
      id
      firstName
      lastName
      email
    }

    templateResponse {
      id
      responseData
      completedAt
      template {
        ... on Template {
          id
          templateData
        }
        ... on LibraryTemplate {
          id
          templateData
        }
      }
    }

    project {
      id
      name
      timezone
      integrationConnections {
        integration {
          integrationType
        }
      }
    }
  }
`

const SITE_DIARY_LIST_ATTRIBUTES = gql`
  fragment siteDiaryListAttributes on SiteDiary {
    id
    createdAt
    archivedAt

    date
    project {
      id
      name
    }

    templateResponse {
      tableBlocks

      attachments {
        id
        url
        fileName
        thumbnail {
          small
        }
      }
    }

    statistics {
      numberOfWorkers
      numberOfSubcontractors
      projectHours
      totalSignOns
      weather
      temperature
      hasUploads
      updatedAt
      projectCompletionPercentage
    }

    templateResponseId
  }
`

export const GET_SITE_DIARIES_LIST = gql`
  query siteDiaries_UI(
    $filter: SiteDiaryFilterInput
    $pagination: PaginationInput
    $sort: SiteDiarySortInput
  ) {
    siteDiaries(filter: $filter, pagination: $pagination, sort: $sort) {
      ...siteDiaryListAttributes
    }
    siteDiariesAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
    mostRecentSiteDiary(filter: $filter) {
      id
      createdAt
      date
    }
  }
  ${SITE_DIARY_LIST_ATTRIBUTES}
`

export const GET_SITE_DIARIES_CSV = gql`
  query siteDiariesCsv_UI(
    $filter: SiteDiaryFilterInput
    $sort: SiteDiarySortInput
  ) {
    siteDiaries(filter: $filter, sort: $sort) {
      ...siteDiaryListAttributes

      templateResponse {
        responseData
      }
    }
  }
  ${SITE_DIARY_LIST_ATTRIBUTES}
`

export const GET_SITE_DIARIES_LIST_FOR_SUMMARY = gql`
  query siteDiariesForSummary_UI(
    $filter: SiteDiaryFilterInput
    $pagination: PaginationInput
    $sort: SiteDiarySortInput
  ) {
    siteDiaries(filter: $filter, pagination: $pagination, sort: $sort) {
      ...siteDiaryAttributes
    }
    siteDiariesAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
    mostRecentSiteDiary(filter: $filter) {
      id
      createdAt
      date
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const GET_SITE_DIARY_BY_DAY = gql`
  query siteDiaryByDay_UI($date: String!, $projectId: String!) {
    siteDiaryByDay(date: $date, projectId: $projectId) {
      ...siteDiaryAttributes
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const GET_SITE_DIARY = gql`
  query siteDiary_UI($id: String!) {
    siteDiary(id: $id) {
      ...siteDiaryAttributes
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const GET_SITE_DIARY_WITH_LINKS = gql`
  query siteDiaryWithLinks_UI($id: String!) {
    siteDiary(id: $id) {
      ...siteDiaryAttributes
      paginationLinks {
        previous {
          id
          date
        }
        next {
          id
          date
        }
      }
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const GET_SITE_DIARY_SIGN_ONS = gql`
  query siteDiarySignOns_UI($id: String!) {
    siteDiary(id: $id) {
      id
      date

      signOns {
        id
        createdAt
        signedOffAt
        manualSignedOnAt
        manualSignedOffAt
        worker {
          id
          user {
            firstName
            lastName
          }
          subcontractors {
            id
            company {
              name
            }
            projects {
              id
            }
          }
        }
        visitor {
          user {
            firstName
            lastName
          }
        }
        signOnHours {
          hours
          updatedAt
        }
      }
    }
  }
`

export const CREATE_SITE_DIARY = gql`
  mutation createSiteDiary_UI($input: CreateSiteDiaryInput!) {
    createSiteDiary(input: $input) {
      ...siteDiaryAttributes
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const UPDATE_SITE_DIARY = gql`
  mutation updateSiteDiary_UI($id: String!, $input: UpdateSiteDiaryInput!) {
    updateSiteDiary(id: $id, input: $input) {
      ...siteDiaryAttributes
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const ARCHIVE_SITE_DIARY = gql`
  mutation archiveSiteDiary_UI($id: String!) {
    archiveSiteDiary(id: $id) {
      ...siteDiaryAttributes
    }
  }
  ${SITE_DIARY_ATTRIBUTES}
`

export const UPSERT_PROCORE_DAILY_LOGS = gql`
  mutation upsertProcoreDailyLogs_UI($input: UpsertProcoreDailyLogsInput) {
    upsertProcoreDailyLogs(input: $input) {
      success
    }
  }
`
